/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  FlatList,
  RefreshControl,
  Alert,
  TextInput,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import {


  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

// Mock course modules data

// Mock reviews data

const CourseDetailsScreen = ({ route, navigation }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [activeTab, setActiveTab] = useState('Overview');
  const { courseData } = route.params || {};

  // Fallback course data if none is provided
  const course = courseData || {
    id: 'course1',
    title: 'Advanced CAD Techniques',
    instructor: 'Dr. Sarah Johnson',
    duration: '6 weeks',
    level: 'Advanced',
    rating: 4.8,
    enrolled: 245,
    image: require('../../../assets/jobeng_icon.png'),
    description:
      'Master advanced CAD techniques for complex engineering projects. Learn parametric modeling, assembly design, and simulation analysis.',
    tags: ['CAD', 'Design', 'Engineering'],
    price: '£299',
    discountPrice: '£249',
    language: 'English',
    lastUpdated: 'May 2025',
    objectives: [
      'Understand advanced parametric modeling techniques',
      'Create complex assemblies with proper constraints',
      'Perform simulation analysis on designed components',
      'Generate professional technical documentation',
      'Implement industry best practices in CAD workflows',
    ],
  };
  const [showModal, setShowModal] = useState(false);
  const [userNote, setUserNote] = useState('');
  // Handle enrollment — route through the real Stripe purchase flow.
  // (Previous implementation just showed a "successful" alert without charging.)
  const handleEnroll = () => {
    const isFreeCourse = course?.isFree === true
      || course?.price === 0
      || course?.price === '0'
      || course?.price === '£0'
      || course?.price === 'Free';

    if (isFreeCourse) {
      Alert.alert(
        'Confirm Enrollment',
        `Are you sure you want to enroll in "${course.title}"?`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Enroll',
            onPress: () =>
              navigation.navigate('CoursePlayerScreen', { courseId: course.id }),
          },
        ]
      );
      return;
    }

    // Paid course → real payment.
    navigation.navigate('CoursePurchaseScreen', {
      course,
      courseId: course?.id,
    });
  };

  // Render stars for rating
  const renderRatingStars = rating => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <MaterialCommunityIcons
            key={`star-${i}`}
            name="star"
            size={16}
            color="#FFC107"
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <MaterialCommunityIcons
            key={`star-${i}`}
            name="star-half"
            size={16}
            color="#FFC107"
          />
        );
      } else {
        stars.push(
          <MaterialCommunityIcons
            key={`star-${i}`}
            name="star-outline"
            size={16}
            color="#FFC107"
          />
        );
      }
    }

    return stars;
  };

  // Render lesson item
  const renderLesson = ({ item }) => (
    <View style={styles.lessonItem}>
      <View style={styles.lessonIconContainer}>
        {item.type === 'video' && (
          <MaterialCommunityIcons
            name="play-circle-outline"
            size={24}
            color="#0e4e96"
          />
        )}
        {item.type === 'reading' && (
          <MaterialCommunityIcons
            name="file-document-outline"
            size={24}
            color="#0e4e96"
          />
        )}
        {item.type === 'interactive' && (
          <MaterialCommunityIcons name="laptop" size={24} color="#0e4e96" />
        )}
        {item.type === 'quiz' && (
          <MaterialCommunityIcons
            name="clipboard-check-outline"
            size={24}
            color="#0e4e96"
          />
        )}
      </View>
      <View style={styles.lessonContent}>
        <Text style={styles.lessonTitle}>{item.title}</Text>
        <Text style={styles.lessonDuration}>{item.duration}</Text>
      </View>
      <MaterialCommunityIcons name="lock-outline" size={20} color="#94a3b8" />
    </View>
  );

  // Render review item
  const renderReview = ({ item }) => (
    <View style={styles.reviewItem}>
      <View style={styles.reviewHeader}>
        <View style={styles.reviewUser}>
          <View style={styles.reviewAvatar}>
            <Text style={styles.reviewAvatarText}>{item.user.charAt(0)}</Text>
          </View>
          <View>
            <Text style={styles.reviewUserName}>{item.user}</Text>
            <Text style={styles.reviewDate}>{item.date}</Text>
          </View>
        </View>
        <View style={styles.reviewRating}>
          {renderRatingStars(item.rating)}
        </View>
      </View>
      <Text style={styles.reviewComment}>{item.comment}</Text>
    </View>
  );

  // Render overview tab content
  const renderOverviewContent = () => (
    <>
      <Text style={styles.sectionTitle}>About This Course</Text>
      <Text style={styles.courseDescription}>{course.description}</Text>

      <Text style={styles.sectionTitle}>What You'll Learn</Text>
      <View style={styles.objectivesList}>
        {course.objectives &&
          course.objectives.map((objective, index) => (
            <View key={`objective-${index}`} style={styles.objectiveItem}>
              <MaterialCommunityIcons
                name="check-circle"
                size={20}
                color="#16a34a"
              />
              <Text style={styles.objectiveText}>{objective}</Text>
            </View>
          ))}
      </View>

      <Text style={styles.sectionTitle}>Course Details</Text>
      <View style={styles.detailsGrid}>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons
            name="clock-outline"
            size={20}
            color="#64748B"
          />
          <View>
            <Text style={styles.detailLabel}>Duration</Text>
            <Text style={styles.detailValue}>{course.duration}</Text>
          </View>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="signal" size={20} color="#64748B" />
          <View>
            <Text style={styles.detailLabel}>Level</Text>
            <Text style={styles.detailValue}>{course.level}</Text>
          </View>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons
            name="account-group"
            size={20}
            color="#64748B"
          />
          <View>
            <Text style={styles.detailLabel}>Enrolled</Text>
            <Text style={styles.detailValue}>{course.enrolled} students</Text>
          </View>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="translate" size={20} color="#64748B" />
          <View>
            <Text style={styles.detailLabel}>Language</Text>
            <Text style={styles.detailValue}>{course.language}</Text>
          </View>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons
            name="calendar-check"
            size={20}
            color="#64748B"
          />
          <View>
            <Text style={styles.detailLabel}>Last Updated</Text>
            <Text style={styles.detailValue}>{course.lastUpdated}</Text>
          </View>
        </View>
      </View>

      <Text style={styles.sectionTitle}>Instructor</Text>
      <View style={styles.instructorCard}>
        <View style={styles.instructorAvatar}>
          <Text style={styles.instructorAvatarText}>
            {course.instructor.charAt(0)}
          </Text>
        </View>
        <View style={styles.instructorInfo}>
          <Text style={styles.instructorName}>{course.instructor}</Text>
          <Text style={styles.instructorTitle}>
            Senior Engineering Instructor
          </Text>
          <View style={styles.instructorStats}>
            <View style={styles.instructorStat}>
              <MaterialCommunityIcons name="star" size={16} color="#FFC107" />
              <Text style={styles.instructorStatText}>
                4.9 Instructor Rating
              </Text>
            </View>
            <View style={styles.instructorStat}>
              <MaterialCommunityIcons
                name="account-group"
                size={16}
                color="#64748B"
              />
              <Text style={styles.instructorStatText}>1,245 Students</Text>
            </View>
            <View style={styles.instructorStat}>
              <MaterialCommunityIcons
                name="book-open-variant"
                size={16}
                color="#64748B"
              />
              <Text style={styles.instructorStatText}>8 Courses</Text>
            </View>
          </View>
        </View>
      </View>
    </>
  );

  // Render curriculum tab content
  const renderCurriculumContent = () => (
    <>
      <View style={styles.curriculumHeader}>
        <View style={styles.curriculumSummary}>
          <Text style={styles.curriculumSummaryText}>
            <Text style={styles.curriculumSummaryBold}>
              {mockModules.length}
            </Text>{' '}
            modules •
            <Text style={styles.curriculumSummaryBold}>
              {' '}
              {mockModules.reduce(
                (total, module) =>
                  total + (module.lessons ? module.lessons.length : 0),
                0
              )}
            </Text>{' '}
            lessons •
            <Text style={styles.curriculumSummaryBold}> 10h 30min</Text> total
            length
          </Text>
        </View>
      </View>

      {mockModules &&
        mockModules.map((module, index) => (
          <View key={module.id} style={styles.moduleContainer}>
            <View style={styles.moduleHeader}>
              <View>
                <Text style={styles.moduleTitle}>{module.title}</Text>
                <Text style={styles.moduleMeta}>
                  {module.lessons ? module.lessons.length : 0} lessons •{' '}
                  {module.duration}
                </Text>
              </View>
              <MaterialCommunityIcons
                name="chevron-down"
                size={24}
                color="#64748B"
              />
            </View>

            <View style={styles.lessonsList}>
              {module.lessons &&
                module.lessons.map(lesson => (
                  <View key={lesson.id}>{renderLesson({ item: lesson })}</View>
                ))}
            </View>
          </View>
        ))}
    </>
  );

  // Render reviews tab content
  const renderReviewsContent = () => (
    <>
      <View style={styles.reviewsSummary}>
        <View style={styles.reviewsRatingContainer}>
          <Text style={styles.reviewsRatingValue}>{course.rating}</Text>
          <View style={styles.reviewsRatingStars}>
            {renderRatingStars(course.rating)}
          </View>
          <Text style={styles.reviewsCount}>
            Based on {mockReviews ? mockReviews.length : 0} reviews
          </Text>
        </View>

        <TouchableOpacity style={styles.writeReviewButton}>
          <Text style={styles.writeReviewButtonText}>Write a Review</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.reviewsList}>
        {mockReviews &&
          mockReviews.map(review => (
            <View key={review.id}>{renderReview({ item: review })}</View>
          ))}
      </View>

      <TouchableOpacity style={styles.viewAllReviewsButton}>
        <Text style={styles.viewAllReviewsText}>View All Reviews</Text>
        <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
      </TouchableOpacity>
    </>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Course Details</Text>
        <JobengLogo />
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Course Hero Section */}
        <View style={styles.courseHero}>
          <Image
            source={course.image}
            style={styles.courseImage}
            resizeMode="cover"
          />
          <View style={styles.courseHeroOverlay}>
            <View style={styles.courseHeroContent}>
              <Text style={styles.courseTitle}>{course.title}</Text>
              <Text style={styles.courseInstructor}>
                By {course.instructor}
              </Text>

              <View style={styles.courseRatingContainer}>
                <Text style={styles.courseRatingValue}>{course.rating}</Text>
                <View style={styles.courseRatingStars}>
                  {renderRatingStars(course.rating)}
                </View>
                <Text style={styles.courseEnrolled}>
                  ({course.enrolled} enrolled)
                </Text>
              </View>

              <View style={styles.courseTags}>
                {course.tags &&
                  course.tags.map((tag, index) => (
                    <View key={`tag-${index}`} style={styles.courseTag}>
                      <Text style={styles.courseTagText}>{tag}</Text>
                    </View>
                  ))}
              </View>
            </View>
          </View>
        </View>

        {/* Price Card */}
        <View style={styles.priceCard}>
          <View style={styles.priceContainer}>
            <Text style={styles.discountPrice}>{course.discountPrice}</Text>
            <Text style={styles.originalPrice}>{course.price}</Text>
            <View style={styles.discountBadge}>
              <Text style={styles.discountBadgeText}>17% OFF</Text>
            </View>
          </View>

          <TouchableOpacity style={styles.enrollButton} onPress={handleEnroll}>
            <Text style={styles.enrollButtonText}>Enroll Now</Text>
          </TouchableOpacity>
        </View>

        {/* Tab Navigation */}
        <View style={styles.tabContainer}>
          {['Overview', 'Curriculum', 'Reviews'].map(tab => (
            <TouchableOpacity
              key={tab}
              style={[
                styles.tabButton,
                activeTab === tab && styles.activeTabButton,
              ]}
              onPress={() => setActiveTab(tab)}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab && styles.activeTabText,
                ]}
              >
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Tab Content */}
        <View style={styles.tabContent}>
          {activeTab === 'Overview' && renderOverviewContent()}
          {activeTab === 'Curriculum' && renderCurriculumContent()}
          {activeTab === 'Reviews' && renderReviewsContent()}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  container: {
    flex: 1,
  },
  courseHero: {
    position: 'relative',
    height: 200,
  },
  courseImage: {
    width: '100%',
    height: '100%',
  },
  courseHeroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.7)',
    justifyContent: 'flex-end',
  },
  courseHeroContent: {
    padding: 16,
  },
  courseTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 14,
    color: '#e2e8f0',
    marginBottom: 8,
  },
  courseRatingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  courseRatingValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
    marginRight: 4,
  },
  courseRatingStars: {
    flexDirection: 'row',
    marginRight: 8,
  },
  courseEnrolled: {
    fontSize: 12,
    color: '#e2e8f0',
  },
  courseTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  courseTag: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginRight: 8,
  },
  courseTagText: {
    fontSize: 12,
    color: '#fff',
  },
  priceCard: {
    backgroundColor: '#fff',
    padding: 16,
    margin: 16,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  discountPrice: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginRight: 8,
  },
  originalPrice: {
    fontSize: 16,
    color: '#94a3b8',
    textDecorationLine: 'line-through',
    marginRight: 8,
  },
  discountBadge: {
    backgroundColor: '#dcfce7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  discountBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#16a34a',
  },
  enrollButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  enrollButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  tabContainer: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  tabButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
  },
  activeTabButton: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  tabContent: {
    padding: 16,
    paddingBottom: 40,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
    marginTop: 16,
  },
  courseDescription: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 22,
    marginBottom: 16,
  },
  objectivesList: {
    marginBottom: 16,
  },
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  objectiveText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 8,
    flex: 1,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '50%',
    marginBottom: 16,
  },
  detailLabel: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 8,
  },
  detailValue: {
    fontSize: 14,
    color: '#0f172a',
    fontWeight: '500',
    marginLeft: 8,
  },
  instructorCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  instructorAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  instructorAvatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  instructorInfo: {
    flex: 1,
  },
  instructorName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  instructorTitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  instructorStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  instructorStat: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 4,
  },
  instructorStatText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  curriculumHeader: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  curriculumSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  curriculumSummaryText: {
    fontSize: 14,
    color: '#64748B',
  },
  curriculumSummaryBold: {
    fontWeight: 'bold',
    color: '#0f172a',
  },
  moduleContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  moduleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  moduleTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  moduleMeta: {
    fontSize: 12,
    color: '#64748B',
  },
  lessonsList: {
    paddingVertical: 8,
  },
  lessonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  lessonIconContainer: {
    marginRight: 12,
  },
  lessonContent: {
    flex: 1,
  },
  lessonTitle: {
    fontSize: 14,
    color: '#334155',
    marginBottom: 2,
  },
  lessonDuration: {
    fontSize: 12,
    color: '#64748B',
  },
  reviewsSummary: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  reviewsRatingContainer: {
    alignItems: 'flex-start',
  },
  reviewsRatingValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  reviewsRatingStars: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  reviewsCount: {
    fontSize: 12,
    color: '#64748B',
  },
  writeReviewButton: {
    backgroundColor: '#f1f5f9',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  writeReviewButtonText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
  },
  reviewsList: {
    marginBottom: 16,
  },
  reviewItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  reviewUser: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  reviewAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#0e4e96',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  reviewAvatarText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  reviewUserName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0f172a',
    marginBottom: 2,
  },
  reviewDate: {
    fontSize: 12,
    color: '#64748B',
  },
  reviewRating: {
    flexDirection: 'row',
  },
  reviewComment: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 20,
  },
  viewAllReviewsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
  },
  viewAllReviewsText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 4,
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 10,
    width: '85%',
    elevation: 10,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#0f172a',
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    padding: 10,
    marginBottom: 16,
    textAlignVertical: 'top',
    height: 80,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    marginHorizontal: 5,
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
});

export default CourseDetailsScreen;
